import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote } from "lucide-react";
import { TESTIMONIALS, PRESS, PRESS_QUOTE } from "../data/events";

const EASE = [0.76, 0, 0.24, 1];

export const Testimonials = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((v) => (v + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="press"
      data-testid="testimonials-section"
      className="relative bg-[color:var(--bt-bg)] py-32 md:py-56 px-6 md:px-10"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* Kicker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-4 mb-16 md:mb-24"
        >
          <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
          <span
            data-testid="testimonials-eyebrow"
            className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
          >
            Chapter IV · Said of us
          </span>
        </motion.div>

        {/* Quote carousel */}
        <div className="grid md:grid-cols-12 gap-6 md:gap-12 items-center min-h-[38vh]">
          <div className="md:col-span-2 flex md:justify-start">
            <Quote size={44} className="text-[color:var(--bt-gold)]" strokeWidth={1} />
          </div>
          <div className="md:col-span-8 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: EASE }}
                data-testid={`testimonial-${active}`}
              >
                <p className="font-serif-display italic text-3xl md:text-6xl leading-[1.1] tracking-tight text-[color:var(--bt-ink)]">
                  “{TESTIMONIALS[active].quote}”
                </p>
                <div className="mt-10 flex items-center gap-4">
                  <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
                  <div>
                    <div className="text-[color:var(--bt-ink)] text-sm">
                      {TESTIMONIALS[active].name}
                    </div>
                    <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mt-1">
                      {TESTIMONIALS[active].role}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="md:col-span-2 flex md:justify-end gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setActive(i)}
                aria-label={`Testimonial ${i + 1}`}
                className={`w-8 h-px transition-colors ${
                  i === active ? "bg-[color:var(--bt-gold)]" : "bg-[color:var(--bt-gold-line)]"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Press strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-24 md:mt-40 border-t border-[color:var(--bt-gold-line)] pt-16 md:pt-20"
          data-testid="press-strip"
        >
          {/* Featured press pull-quote */}
          <div className="max-w-4xl mb-16 md:mb-24">
            <p
              data-testid="press-quote"
              className="font-serif-display italic text-2xl md:text-4xl leading-[1.3] tracking-tight text-[color:var(--bt-ink)]"
            >
              "{PRESS_QUOTE.quote}"
            </p>
            <div className="mt-6 flex items-center gap-4">
              <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
              <span
                data-testid="press-quote-source"
                className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
              >
                {PRESS_QUOTE.source}
              </span>
            </div>
          </div>

          <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-10">
            As remarked in
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-16 gap-y-12 md:px-6">
            {PRESS.map((p) => (
              <div
                key={p.id}
                data-testid={`press-${p.id}`}
                className="opacity-70 hover:opacity-100 transition-opacity duration-500"
              >
                <PressLogo id={p.id} name={p.name} />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/**
 * PressLogo - clean high-contrast SVG masthead wordmarks. Rendered as SVG
 * text in the publication's typographic register (Didone/serif).
 */
const PressLogo = ({ id, name }) => {
  const common = {
    fill: "var(--bt-ink)",
    fontFamily: "'Cormorant Garamond', serif",
    textAnchor: "middle",
    dominantBaseline: "middle",
  };
  if (id === "vogue") {
    return (
      <svg viewBox="0 0 160 44" width="150" height="42" role="img" aria-label="Vogue">
        <text x="80" y="24" {...common} fontSize="40" fontWeight="500" letterSpacing="6">
          VOGUE
        </text>
      </svg>
    );
  }
  if (id === "tatler") {
    return (
      <svg viewBox="0 0 170 44" width="150" height="40" role="img" aria-label="Tatler">
        <text x="85" y="24" {...common} fontSize="34" fontWeight="600" letterSpacing="7">
          TATLER
        </text>
      </svg>
    );
  }
  if (id === "the-times") {
    return (
      <svg viewBox="0 0 170 52" width="150" height="46" role="img" aria-label="The Times">
        <text x="85" y="13" {...common} fontSize="12" fontWeight="500" letterSpacing="5">
          THE
        </text>
        <text x="85" y="36" {...common} fontSize="30" fontWeight="600" letterSpacing="2">
          TIMES
        </text>
      </svg>
    );
  }
  // conde-nast-traveller
  return (
    <svg viewBox="0 0 220 52" width="190" height="46" role="img" aria-label="Condé Nast Traveller">
      <text x="110" y="16" {...common} fontSize="18" fontWeight="600" letterSpacing="3">
        CONDÉ NAST
      </text>
      <text x="110" y="38" {...common} fontSize="15" fontWeight="400" fontStyle="italic" letterSpacing="4">
        Traveller
      </text>
    </svg>
  );
};

export default Testimonials;
